import { SafeDictionary } from "@insite/client-framework/Common/Types";
import replaceUrlsWithCdnUrls from "@insite/client-framework/Common/Utilities/replaceUrlsWithCdnUrls";
import { setShellCatalogContext } from "@insite/client-framework/Common/Utilities/shellCatalogContext";
import { initializeSiteHole } from "@insite/client-framework/Components/ShellHole";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    selectBrand,
    selectCategory,
    selectProduct,
    setCMSPermissions,
    setSearchDataModeActive,
} from "@insite/client-framework/Store/Context/ContextActionCreators";
import setLanguage from "@insite/client-framework/Store/Context/Handlers/SetLanguage";
import {
    beginDraggingWidget,
    endDraggingWidget,
    resetPageDataViews,
    setPageDefinitions,
    updatePage,
} from "@insite/client-framework/Store/Data/Pages/PagesActionCreators";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { loadPageLinks } from "@insite/client-framework/Store/Links/LinksActionCreators";
import { PageDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { PageModel } from "@insite/client-framework/Types/PageProps";
import PermissionsModel from "@insite/client-framework/Types/PermissionsModel";
import { cleanupAfterDragging } from "@insite/client-framework/WidgetReordering";
import { History } from "@insite/mobius/utilities/HistoryContext";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    history: History;
}

const mapStateToProps = (state: ApplicationState) => ({
    page: getCurrentPage(state),
    pathname: getLocation(state).pathname,
    searchPath: state.context.settings.settingsCollection.searchSettings.searchPath,
    enableCdnPrefix: state.context.settings.settingsCollection.websiteSettings.enableCdnPrefix,
    cdnPrefix: state.context.settings.settingsCollection.websiteSettings.cdnPrefix,
});

const mapDispatchToProps = {
    beginDraggingWidget,
    endDraggingWidget,
    setLanguage,
    selectProduct,
    selectCategory,
    selectBrand,
    loadPageLinks,
    setCMSPermissions,
    setPageDefinitions,
    resetPageDataViews,
    setSearchDataModeActive,
    updatePage,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

interface MainNavigation {
    close: () => void;
    openMenu: (index: number) => void;
}

let mainNavigation: MainNavigation;

export const setMainNavigation = (value: MainNavigation) => {
    mainNavigation = value;
};

class ShellHoleProvider extends React.Component<Props> {
    constructor(props: Props) {
        super(props);
        const {
            updatePage,
            beginDraggingWidget,
            endDraggingWidget,
            setLanguage,
            selectProduct,
            selectCategory,
            selectBrand,
            loadPageLinks,
            resetPageDataViews,
            setCMSPermissions,
            setPageDefinitions,
            setSearchDataModeActive,
        } = this.props;
        const doInitialization = () => {
            initializeSiteHole({
                LoadUrl: ({ url }: { url: string }) => {
                    props.history.push(url);
                },
                UpdatePage: ({ page }: { page: PageModel }) => {
                    updatePage(this.replaceCdnPrefix(page));
                },
                BeginDraggingWidget: ({ id }: { id: string }) => {
                    beginDraggingWidget(id);
                },
                EndDraggingWidget: () => {
                    cleanupAfterDragging();
                    endDraggingWidget();
                },
                ChangeLanguage: ({ languageId }: { languageId: string }) => {
                    setLanguage({ languageId });
                },
                SelectProduct: ({ productPath }: { productPath: string }) => {
                    selectProduct(productPath);
                },
                SelectCategory: ({ categoryPath }: { categoryPath: string }) => {
                    selectCategory(categoryPath);
                },
                SelectBrand: ({ brandPath }: { brandPath: string }) => {
                    selectBrand(brandPath);
                },
                CloseMainNavigation: () => {
                    mainNavigation?.close();
                },
                OpenMainNavigation: ({ index }: { index: number }) => {
                    mainNavigation?.openMenu(index);
                },
                Reload: () => {
                    window.location.reload();
                },
                ReloadPageLinks: () => {
                    loadPageLinks();
                },
                ResetPageDataViews: () => {
                    resetPageDataViews();
                },
                CMSPermissions: ({
                    permissions,
                    canChangePage,
                }: {
                    permissions: PermissionsModel;
                    canChangePage: boolean;
                }) => {
                    setCMSPermissions(permissions, canChangePage);
                },
                PageDefinitions: ({
                    pageDefinitionsByType,
                }: {
                    pageDefinitionsByType: SafeDictionary<Pick<PageDefinition, "pageType">>;
                }) => {
                    setPageDefinitions(pageDefinitionsByType);
                },
                SearchDataModeActive: ({ active }: { active: boolean }) => {
                    setSearchDataModeActive(active);
                },
            });
        };
        if (!IS_SERVER_SIDE) {
            if (window.document.readyState === "complete") {
                doInitialization();
            } else {
                document.addEventListener("readystatechange", (event: any) => {
                    if (event.target.readyState === "complete") {
                        doInitialization();
                    }
                });
            }
        }
    }

    componentDidMount() {
        this.storeCatalogContext();
    }

    componentDidUpdate() {
        this.storeCatalogContext();
    }

    storeCatalogContext() {
        const { page, pathname, searchPath } = this.props;
        const pathnameLowerCase = pathname.toLowerCase();

        if (pathnameLowerCase.startsWith("/content/") || pathnameLowerCase.startsWith("/.spire/")) {
            return;
        }

        switch (page.type) {
            case "ProductDetailsPage":
                setShellCatalogContext({ productPath: pathname });
                break;
            case "BrandDetailsPage":
                setShellCatalogContext({ brandPath: pathname });
                break;
            case "CategoryDetailsPage":
                setShellCatalogContext({ categoryPath: pathname });
                break;
            case "ProductListPage":
                setShellCatalogContext(
                    searchPath && pathnameLowerCase.startsWith(`/${searchPath.toLowerCase()}`)
                        ? {}
                        : { categoryPath: pathname },
                );
                break;
            default:
                setShellCatalogContext({});
                break;
        }
    }

    replaceCdnPrefix(pageModel: PageModel) {
        const { enableCdnPrefix, cdnPrefix } = this.props;
        if (enableCdnPrefix) {
            const pageModelAsJson = replaceUrlsWithCdnUrls(cdnPrefix, JSON.stringify(pageModel));
            return JSON.parse(pageModelAsJson);
        }
        return pageModel;
    }

    render() {
        return null;
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(ShellHoleProvider);
